import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { FiX, FiArrowLeft } from "react-icons/fi";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from "react-google-places-autocomplete";
import PhoneInput from "react-phone-input-2";
import { appConstants } from "../../../redux/action-constants/inapp.constants";
import { AlertMsg } from "../alert-msg";
import "./UpdateCustomerModal.scss";

interface UpdateCustomerModalProps {
  show: boolean;
  onHide: () => void;
  customerData?: any;
  onUpdate?: (data: any) => void;
  updateStatus?: any;
}

const UpdateCustomerModal: React.FC<UpdateCustomerModalProps> = ({
  show,
  onHide,
  customerData,
  onUpdate,
  updateStatus,
}) => {
  const [addressSearched, setAddressSearched] = useState<any>(null);

  // Use a ref for onHide so the effect never re-fires just because the parent
  // recreated its handler - the ONLY trigger should be success becoming true.
  const onHideRef = React.useRef(onHide);
  onHideRef.current = onHide;

  useEffect(() => {
    if (updateStatus?.success) {
      const timer = setTimeout(() => {
        onHideRef.current();
      }, 2000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateStatus?.success]);

  // Address Extraction Utility: "567 St Anne's Rd unit 4" -> "4-567 St Anne's Rd"
  const formatAddressWithUnit = (address: string) => {
    if (!address) return "";
    const unitRegex = /(.*?)\s+(?:unit|apt|#|suite)\s+([a-zA-Z0-9]+)/i;
    const match = address.match(unitRegex);
    if (match) {
      const [, street, unit] = match;
      return `${unit}-${street.trim()}`;
    }
    return address;
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    phoneNumber: Yup.string().required("Phone number is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    streetAddress: Yup.string().required("Address is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("Province/State is required"),
    postalCode: Yup.string().required("Postal code is required"),
    country: Yup.string().required("Country is required"),
  });

  const getInitialValues = () => {
    let firstName = customerData?.firstName || "";
    let lastName = customerData?.lastName || "";

    // Name Splitting Logic
    if (!firstName && !lastName && customerData?.name) {
      const parts = customerData.name.trim().split(/\s+/);
      firstName = parts[0];
      lastName = parts.slice(1).join(" ");
    }

    return {
      firstName: firstName,
      lastName: lastName,
      phoneNumber: customerData?.phoneNumber || customerData?.phone || "",
      email: customerData?.email || "",
      streetAddress:
        customerData?.streetAddress ||
        customerData?.address?.streetAddress ||
        "",
      city: customerData?.city || customerData?.address?.city || "",
      state: customerData?.state || customerData?.address?.state || "",
      postalCode:
        customerData?.postalCode || customerData?.address?.postalCode || "",
      country:
        customerData?.country || customerData?.address?.country || "Canada",
      latitude: customerData?.latitude || 0,
      longitude: customerData?.longitude || 0,
    };
  };

  const handleSelectedAddress = async (
    selection: any,
    formikHelpers: {
      setValues: (values: any) => void;
      values: any;
    },
  ) => {
    const { setValues, values } = formikHelpers;
    try {
      setAddressSearched(selection);
      if (!selection) return;

      const [place] = await geocodeByPlaceId(selection?.value.place_id);
      const components = place?.address_components || [];

      let streetNumber = "";
      let routeName = "";
      let locality = "";
      let adminLevel2 = "";
      let adminLevel1Long = "";
      let countryLong = "";
      let postalCode = "";

      for (const comp of components) {
        if (comp.types.includes("street_number")) streetNumber = comp.long_name;
        if (comp.types.includes("route")) routeName = comp.long_name;
        if (comp.types.includes("locality")) locality = comp.long_name;
        if (comp.types.includes("administrative_area_level_2"))
          adminLevel2 = comp.long_name;
        if (comp.types.includes("administrative_area_level_1"))
          adminLevel1Long = comp.long_name;
        if (comp.types.includes("country")) countryLong = comp.long_name;
        if (comp.types.includes("postal_code")) postalCode = comp.long_name;
      }

      const streetAddress = [streetNumber, routeName].filter(Boolean).join(" ");

      // Extract precise coordinates from the geocoded result
      const lat = place?.geometry?.location?.lat();
      const lng = place?.geometry?.location?.lng();

      setValues({
        ...values,
        streetAddress,
        city: locality || adminLevel2,
        state: adminLevel1Long,
        country: countryLong,
        postalCode: postalCode,
        latitude: typeof lat === "number" ? lat : values.latitude,
        longitude: typeof lng === "number" ? lng : values.longitude,
      });
    } catch (err) {
      console.error("Address parsing error:", err);
    }
  };

  const handleSubmit = (values: any) => {
    // For address fields, fall back to customerData.address sub-object if the
    // Formik field is empty (happens when the user doesn't touch the autocomplete)
    const resolveAddress = (
      formVal: string,
      key: keyof typeof customerData.address,
    ) => formVal || customerData?.address?.[key] || "";

    const streetRaw =
      values.streetAddress || customerData?.address?.streetAddress || "";
    const finalValues = {
      firstName: values.firstName || "",
      lastName: values.lastName || "",
      phoneNumber: values.phoneNumber || "",
      dob: customerData?.dob || new Date().toISOString(),
      profileImageUrl: customerData?.profileImageUrl || "string",
      postalCode: resolveAddress(values.postalCode, "postalCode"),
      streetAddress: formatAddressWithUnit(streetRaw),
      city: resolveAddress(values.city, "city"),
      country: resolveAddress(values.country, "country") || "Canada",
      howDidYouHearAboutUs: customerData?.howDidYouHearAboutUs || "string",
      longitude: values.longitude || customerData?.longitude || 0,
      latitude: values.latitude || customerData?.latitude || 0,
      state: resolveAddress(values.state, "state"),
      id: customerData?.id,
    };

    if (onUpdate) {
      onUpdate(finalValues);
    }
    // Removed onHide() to allow feedback visibility (success/error)
  };

  const accountStatus = customerData?.status || "Active";

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="update-customer-modal"
    >
      <Modal.Header>
        <Modal.Title>Update customer's information</Modal.Title>
        <button className="close-button" onClick={onHide}>
          <FiX />
        </button>
      </Modal.Header>

      <Modal.Body>
        {updateStatus?.error && (
          <AlertMsg type="error" message={updateStatus.error} />
        )}
        {updateStatus?.success && (
          <AlertMsg type="success" message="Successful" />
        )}
        <div className="account-status">
          Account status: <span className="status-active">{accountStatus}</span>
        </div>

        <Formik
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            setValues,
            handleSubmit,
            isSubmitting,
            submitForm,
          }: any) => (
            <form
              className="form-grid"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(e);
              }}
            >
              <div className="form-group">
                <label>First name</label>
                <Field
                  type="text"
                  name="firstName"
                  className={`form-control ${errors.firstName && touched.firstName ? "is-invalid" : ""}`}
                />
                <ErrorMessage
                  name="firstName"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              <div className="form-group">
                <label>Last name</label>
                <Field
                  type="text"
                  name="lastName"
                  className={`form-control ${errors.lastName && touched.lastName ? "is-invalid" : ""}`}
                />
                <ErrorMessage
                  name="lastName"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              <div className="form-group">
                <label>Phone number</label>
                <PhoneInput
                  country={"ca"}
                  value={values.phoneNumber}
                  onChange={(phone) => setFieldValue("phoneNumber", phone)}
                  inputClass={`form-control ${errors.phoneNumber && touched.phoneNumber ? "is-invalid" : ""}`}
                  containerClass="phone-input-container"
                />
                <ErrorMessage
                  name="phoneNumber"
                  component="div"
                  className="invalid-feedback d-block"
                />
              </div>

              <div className="form-group">
                <label>Email address</label>
                <Field
                  type="email"
                  name="email"
                  className={`form-control ${errors.email && touched.email ? "is-invalid" : ""}`}
                />
                <ErrorMessage
                  name="email"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              <div className="form-group form-group-full">
                <label>Home address</label>
                <div
                  className={`address-autocomplete-wrapper ${errors.streetAddress && touched.streetAddress ? "is-invalid" : ""}`}
                >
                  <GooglePlacesAutocomplete
                    selectProps={{
                      value:
                        addressSearched ||
                        (values.streetAddress
                          ? {
                              label: values.streetAddress,
                              value: { manual: true },
                            }
                          : null),
                      onChange: (val) =>
                        handleSelectedAddress(val, { setValues, values }),
                      placeholder: "Enter address",
                      isClearable: true,
                      classNamePrefix: "address-select",
                    }}
                    apiKey={appConstants.GOOGLE_MAP_KEY}
                    autocompletionRequest={{
                      componentRestrictions: { country: ["ca"] },
                      types: ["address"],
                    }}
                  />
                </div>
                <ErrorMessage
                  name="streetAddress"
                  component="div"
                  className="invalid-feedback d-block"
                />
              </div>

              <div className="form-group">
                <label>City</label>
                <Field
                  type="text"
                  name="city"
                  className={`form-control ${errors.city && touched.city ? "is-invalid" : ""}`}
                />
                <ErrorMessage
                  name="city"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              <div className="form-group">
                <label>State</label>
                <Field
                  type="text"
                  name="state"
                  className={`form-control ${errors.state && touched.state ? "is-invalid" : ""}`}
                />
                <ErrorMessage
                  name="state"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              <div className="form-group">
                <label>Postal code</label>
                <Field
                  type="text"
                  name="postalCode"
                  className={`form-control ${errors.postalCode && touched.postalCode ? "is-invalid" : ""}`}
                />
                <ErrorMessage
                  name="postalCode"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              <div className="form-group">
                <label>Country</label>
                <Field
                  type="text"
                  name="country"
                  className={`form-control ${errors.country && touched.country ? "is-invalid" : ""}`}
                />
                <ErrorMessage
                  name="country"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              <div className="modal-footer-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={onHide}
                >
                  <FiArrowLeft />
                  Go back
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={updateStatus?.loading}
                  onClick={() => submitForm()}
                >
                  {updateStatus?.loading
                    ? "Updating..."
                    : "Update customer's account"}
                </button>
              </div>
            </form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default UpdateCustomerModal;
